import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { addMonths } from 'date-fns'
import { ApiClient } from '@/utils'
import { COOKIE_KEYS } from '@/constants'
import {
  setSecureCookie,
  getSecureCookie,
  removeSecureCookie,
  debugCookies
} from '@/utils/cookie-utils'

export type User = {
  id: string
  name: string
  username: string
  email: string
  mobile: string
  score: number
}

export interface AuthState {
  user: User | null
  isAuthenticated: boolean
  hasAccess: boolean
  errors: Record<string, string[]>
}

export const useAuthStore = defineStore(
  'auth',
  () => {
    let refreshTimer: ReturnType<typeof setInterval> | undefined
    const sessionGeneration = ref(0)
    const getSessionGeneration = () => sessionGeneration.value
    const state = ref<AuthState>({
      user: JSON.parse(localStorage.getItem('userInfo') || 'null'),
      isAuthenticated: !!getStoredToken(),
      hasAccess: JSON.parse(localStorage.getItem('hasAccess') || 'false'),
      errors: {}
    })

    // Helper function to get token from multiple storage methods
    function getStoredToken(): string | null {
      // Debug logging
      debugCookies()

      // Retrieve token using secure method
      const cookieToken = getSecureCookie(COOKIE_KEYS.userToken)

      return cookieToken || null
    }

    const isAuthenticated = computed(() => state.value.isAuthenticated)
    const hasAccess = computed(() => state.value.hasAccess)
    const user = computed(() => state.value.user)
    const errors = computed(() => state.value.errors)

    function setHasAccess(hasAccess: boolean) {
      state.value.hasAccess = hasAccess
      localStorage.setItem('hasAccess', JSON.stringify(hasAccess))
    }

    function setToken(token: string) {
      const tokenExpireDate = addMonths(new Date(), 12)

      try {
        // Use secure cookie setting method
        setSecureCookie(COOKIE_KEYS.userToken, token, {
          expires: tokenExpireDate
        })
      } catch (error) {
        console.error('Failed to set authentication token', error)
        throw new Error('Token storage failed')
      }
    }

    function setUserFullname({
      fullname,
      callback
    }: {
      fullname: string
      callback?: () => void
    }) {
      try {
        if (state.value.user) {
          state.value.user.name = fullname
          // Update localStorage with the user name(name not username!)
          localStorage.setItem('userInfo', JSON.stringify(state.value.user))
        }

        if (callback && typeof callback === 'function') {
          callback()
        }
      } catch (error) {
        console.error('Error setting userFullname:', error)
        throw error
      }
    }

    function setAuth({
      token,
      hasAccess,
      userInfo,
      callback
    }: {
      token: string
      hasAccess: boolean
      userInfo: User
      callback?: () => void
    }) {
      try {
        // Invalidate callbacks belonging to the previous session.
        sessionGeneration.value++
        setToken(token)

        // Update state
        state.value.user = userInfo
        setHasAccess(hasAccess)
        state.value.isAuthenticated = true
        state.value.errors = {}

        // Store user info and access state
        localStorage.setItem('userInfo', JSON.stringify(userInfo))

        // Store the initial set time
        localStorage.setItem('tokenSetTime', new Date().toISOString())

        ApiClient.setToken(token)

        // Set up periodic token refresh
        setupTokenRefresh()

        if (callback && typeof callback === 'function') {
          callback()
        }
      } catch (error) {
        console.error('Error setting authentication:', error)
        throw error
      }
    }

    // Set up periodic check to refresh the cookie
    function setupTokenRefresh() {
      // Check and refresh token every 6 hours
      const SIX_HOURS = 6 * 60 * 60 * 1000

      if (refreshTimer) clearInterval(refreshTimer)
      refreshTimer = setInterval(() => {
        const token = getStoredToken()
        if (token) {
          // Re-set the cookie to refresh its expiration
          setToken(token)
        }
      }, SIX_HOURS)
    }

    function restoreAuthState() {
      const token = getStoredToken()
      const storedHasAccess = localStorage.getItem('hasAccess')
      const storedUserInfo = localStorage.getItem('userInfo')

      if (token) {
        // Refresh the cookie expiration
        setToken(token)

        state.value.isAuthenticated = true
        state.value.hasAccess = storedHasAccess ? JSON.parse(storedHasAccess) : false
        state.value.user = storedUserInfo ? JSON.parse(storedUserInfo) : null

        ApiClient.setToken(token)

        // Set up refresh mechanism
        setupTokenRefresh()
      } else {
        purgeAuth()
      }
    }

    function purgeAuth(callback?: () => void) {
      sessionGeneration.value++
      if (refreshTimer) clearInterval(refreshTimer)
      refreshTimer = undefined
      state.value.user = null
      state.value.isAuthenticated = false
      state.value.hasAccess = false
      state.value.errors = {}
      const failures: unknown[] = []
      const attempt = (action: () => void) => {
        try { action() } catch (error) { failures.push(error) }
      }
      attempt(() => removeSecureCookie(COOKIE_KEYS.userToken))
      attempt(() => localStorage.removeItem(COOKIE_KEYS.userToken))
      attempt(() => sessionStorage.removeItem(COOKIE_KEYS.userToken))
      for (const key of ['hasAccess', 'userInfo', 'tokenSetTime', 'auth-store']) {
        attempt(() => localStorage.removeItem(key))
      }
      attempt(() => ApiClient.removeToken())
      if (callback) attempt(callback)
      if (failures.length) console.error('Error purging authentication:', failures)
    }

    const logout = () => purgeAuth()

    // Initialize token refresh mechanism if token exists
    if (getStoredToken()) {
      setupTokenRefresh()
    }

    return {
      isAuthenticated,
      getSessionGeneration,
      sessionGeneration,
      hasAccess,
      user,
      errors,
      setAuth,
      setHasAccess,
      setUserFullname,
      purgeAuth,
      logout,
      restoreAuthState
    }
  },
  {
    persist: {
      key: 'auth-store',
      paths: ['user', 'isAuthenticated', 'hasAccess']
    }
  }
)
